/**
 * Shared plan-task status vocabulary for the plan card surfaces (plan-cards-spec §Estados de
 * tarea and §Pasos agrupados): the icon/tone/label copy for every task state and the
 * order-independent aggregation rule for one step's task states. Pure module — no Angular
 * dependencies, no side effects. The human label is tooltip-only copy; raw state ids never
 * render as visible text.
 */

import type { InterruptPayload } from "../../../../model/interrupt.types";

/**
 * Live lifecycle state of one plan task, as reported by the engine (plan-cards-spec §Estados de
 * tarea). Raw `ActionState` values ride the full EngineRunState dumps and the durable plan
 * block; `"WAITING"` is the frontend-side collapsed state synthesized from the status contract
 * v3 task-level `waiting` diff (the wire no longer distinguishes the park variants — the
 * pending-reason `detail` carries the specifics).
 */
export type PlanTaskState = "PENDING" | "READY" | "DONE" | "FAILED" | "WAITING_USER" | "WAITING_INPUT" | "WAITING";

/** Semantic tone of a status icon — consuming components map tones to token-backed color classes. */
export type PlanStatusTone = "ok" | "err" | "warn" | "idle" | "pri";

/** Icon-only status copy for a task state: Material icon ligature, semantic tone and human label. */
export interface PlanStatusVisual {
  icon: string;
  tone: PlanStatusTone;
  label: string;
}

/**
 * Icon/tone/label copy for one plan task state (plan-cards-spec §Estados de tarea): PENDING
 * `schedule` idle, READY `play_circle_outline` primary, DONE `check_circle` ok, FAILED `error`
 * err, WAITING_USER `person` warn, WAITING_INPUT `keyboard` warn, and the collapsed WAITING
 * `person` warn (status contract v3 — the wire parks are indistinguishable, so the generic
 * waiting visual carries the reason via the pending-detail line). The label rides the status
 * icon's tooltip (V-series ruling: state words live in tooltips, never as row text). Unknown
 * states — impossible per the union, defensive only — log and render as a neutral `help` icon.
 */
export function planTaskStatusVisual(state: PlanTaskState, frontendTool = false): PlanStatusVisual {
  if (frontendTool && (state === "WAITING_USER" || state === "WAITING")) {
    // The browser executes the tool on its own — nothing is pending on the person.
    return { icon: "build_circle", tone: "pri", label: "Running tool in the browser" };
  }
  switch (state) {
    case "PENDING":
      return { icon: "schedule", tone: "idle", label: "Pending" };
    case "READY":
      return { icon: "play_circle_outline", tone: "pri", label: "Ready" };
    case "DONE":
      return { icon: "check_circle", tone: "ok", label: "Done" };
    case "FAILED":
      return { icon: "error", tone: "err", label: "Failed" };
    case "WAITING_USER":
      return { icon: "person", tone: "warn", label: "Waiting for user" };
    case "WAITING_INPUT":
      return { icon: "keyboard", tone: "warn", label: "Waiting for input" };
    case "WAITING":
      return { icon: "person", tone: "warn", label: "Waiting" };
    default: {
      console.error("plan-task-status: unexpected task state", state);
      return { icon: "help", tone: "idle", label: "Unknown" };
    }
  }
}

/**
 * Task ids parked on a `frontend_tools` interrupt — the browser runs those tools itself, so
 * the task is not waiting for the person. Accepts any interrupt payload (live HITL payload,
 * run suspension or a task's pending-reason payload); null/other payload types yield none.
 */
export function frontendToolTaskIds(payload: InterruptPayload | null | undefined): string[] {
  if (payload?.type !== "frontend_tools") return [];
  return payload.items.map((item) => item.task_id);
}

/**
 * Order-independent aggregate state of one step's tasks (plan-cards-spec §Pasos agrupados):
 * any FAILED dominates; then all-DONE (and at least one task); then any task parked on the
 * user/input (reported as WAITING_USER regardless of which waiting variant — the collapsed
 * WAITING included); then any READY; otherwise PENDING. Tasks may complete out of order (e.g.
 * `step_2_task_1` DONE while `step_1_task_1` is retrying), so no sequencing is assumed — every
 * state is read only from its own datum. An empty list aggregates to PENDING.
 */
export function aggregatePlanTaskStatus(states: readonly PlanTaskState[]): PlanTaskState {
  if (states.length === 0) {
    return "PENDING";
  }
  if (states.includes("FAILED")) {
    return "FAILED";
  }
  if (states.every((state) => state === "DONE")) {
    return "DONE";
  }
  if (states.some((state) => state === "WAITING_USER" || state === "WAITING_INPUT" || state === "WAITING")) {
    return "WAITING_USER";
  }
  if (states.includes("READY")) {
    return "READY";
  }
  return "PENDING";
}
